"use client"

import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

import {
  CustomFieldEntity,
  CustomFieldValueType,
  type CustomFieldTemplate,
} from "@/shared/lib/attributes/custom-fields"

type CreateCustomFieldTemplateInput = {
  entity: CustomFieldEntity
  label: string
  valueType: CustomFieldValueType
  isRequired: boolean
  isActive: boolean
}

type UpdateCustomFieldTemplateInput = {
  label: string
  isRequired: boolean
  isActive: boolean
}

type CustomFieldTemplatesState = {
  templates: CustomFieldTemplate[]
  isLoading: boolean
  error: string | null
  createTemplate: (input: CreateCustomFieldTemplateInput) => Promise<CustomFieldTemplate>
  updateTemplate: (id: string, input: UpdateCustomFieldTemplateInput) => Promise<CustomFieldTemplate>
  setTemplateActive: (id: string, isActive: boolean) => Promise<CustomFieldTemplate>
  getActiveTemplatesByEntity: (entity: CustomFieldEntity) => CustomFieldTemplate[]
}

const INITIAL_TEMPLATES: CustomFieldTemplate[] = []

function slugify(input: string) {
  const base = input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
  return base || "field"
}

function buildUniqueKey(templates: CustomFieldTemplate[], label: string) {
  const base = slugify(label)
  if (!templates.some((item) => item.key === base)) return base

  let suffix = 2
  let next = `${base}_${suffix}`
  while (templates.some((item) => item.key === next)) {
    suffix += 1
    next = `${base}_${suffix}`
  }
  return next
}

function buildId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `cft_${Math.random().toString(36).slice(2, 10)}`
}

function emulateDelay() {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, 120)
  })
}

export const useCustomFieldTemplatesStore = create<CustomFieldTemplatesState>()(
  persist(
    (set, get) => ({
  templates: INITIAL_TEMPLATES,
  isLoading: false,
  error: null,

  getActiveTemplatesByEntity: (entity) => {
    return get().templates.filter((item) => item.entity === entity && item.isActive)
  },

  createTemplate: async (input) => {
    if (input.label.trim().length < 2) {
      throw new Error("errors.labelMin")
    }

    set({ isLoading: true, error: null })
    try {
      await emulateDelay()
      const now = new Date().toISOString()
      const current = get().templates
      const created: CustomFieldTemplate = {
        id: buildId(),
        entity: input.entity,
        key: buildUniqueKey(current, input.label),
        label: input.label.trim(),
        valueType: input.valueType,
        isRequired: input.isRequired,
        isActive: input.isActive,
        createdAt: now,
        updatedAt: now,
      }

      set((state) => ({
        templates: [created, ...state.templates],
        isLoading: false,
        error: null,
      }))

      return created
    } catch (error) {
      const message = error instanceof Error ? error.message : "errors.createFailed"
      set({ isLoading: false, error: message })
      throw new Error(message)
    }
  },

  updateTemplate: async (id, input) => {
    const existing = get().templates.find((item) => item.id === id)
    if (!existing) throw new Error("errors.notFound")
    if (input.label.trim().length < 2) throw new Error("errors.labelMin")

    set({ isLoading: true, error: null })

    try {
      await emulateDelay()
      const updated: CustomFieldTemplate = {
        ...existing,
        label: input.label.trim(),
        isRequired: input.isRequired,
        isActive: input.isActive,
        updatedAt: new Date().toISOString(),
      }

      set((state) => ({
        templates: state.templates.map((item) => (item.id === id ? updated : item)),
        isLoading: false,
        error: null,
      }))

      return updated
    } catch (error) {
      const message = error instanceof Error ? error.message : "errors.updateFailed"
      set({ isLoading: false, error: message })
      throw new Error(message)
    }
  },

  setTemplateActive: async (id, isActive) => {
    const existing = get().templates.find((item) => item.id === id)
    if (!existing) throw new Error("errors.notFound")

    set({ isLoading: true, error: null })
    try {
      await emulateDelay()
      const updated: CustomFieldTemplate = {
        ...existing,
        isActive,
        updatedAt: new Date().toISOString(),
      }

      set((state) => ({
        templates: state.templates.map((item) => (item.id === id ? updated : item)),
        isLoading: false,
        error: null,
      }))

      return updated
    } catch (error) {
      const message = error instanceof Error ? error.message : "errors.statusUpdateFailed"
      set({ isLoading: false, error: message })
      throw new Error(message)
    }
  },
    }),
    {
      name: "custom-field-templates-store-v1",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        templates: state.templates,
      }),
    }
  )
)
